import { atom } from 'jotai';
import { atomWithLazy, loadable } from 'jotai/utils';
import { ICompanyDataModel } from '../../data-models/company.data-model';
import { fetchAllPortCos } from '../queries/MaggieCompanyQueries';
import { companySyncCacheAtom } from './CompanyStateJ';

export const portfolioCompaniesAtom = atomWithLazy(fetchAllPortCos);

/**
 * Loadable view of the same fetch, for callers that render before it resolves.
 *
 * Declared once here rather than wrapped per consumer: `loadable()` creates a new derived
 * atom each call, so two module-level wrappers mean two subscriptions to the same data.
 */
export const portfolioCompaniesLoadableAtom = loadable(portfolioCompaniesAtom);

export const portcoIdsStateJ = atom<number[] | null>(null);

export const selectedCompanyIdStateFinanceJ = atom<number | null>(null);

/**
 * Derived list of resolved portfolio companies.
 *
 * Subscribes to `portcoIdsStateJ` and the sync mirror of `companyStateJ`,
 * so the list stays in sync with single-company edits (cross-store reactivity).
 *
 * Reads from `companySyncCacheAtom` (rather than `companyStateJ` directly) to
 * avoid triggering the per-id auto-fetch on cold reads — see PR 8b's N+1 fix.
 */
export const portcosAtomJ = atom<ICompanyDataModel[]>((get) => {
  const ids = get(portcoIdsStateJ);
  if (!ids) {
    return [];
  }
  const cache = get(companySyncCacheAtom);
  const out: ICompanyDataModel[] = [];
  for (const id of ids) {
    const company = cache.get(id);
    if (company) {
      out.push(company);
    }
  }
  return out;
});

export const portcosByIdMapAtomJ = atom<Map<number, ICompanyDataModel>>((get) => {
  const map = new Map<number, ICompanyDataModel>();
  for (const c of get(portcosAtomJ)) {
    map.set(c.id, c);
  }
  return map;
});

export const portcosByNameMapAtomJ = atom<Map<string, ICompanyDataModel>>((get) => {
  const map = new Map<string, ICompanyDataModel>();
  for (const c of get(portcosAtomJ)) {
    map.set(c.name, c);
  }
  return map;
});
